import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";

const carouselCards = [
  {
    id: 1,
    image: "/images/expodemo/step1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the belly of your cat. A page will open, click <span class='font-bold text-black'>Claim now or Sign up</span>.",
  },
  {
    id: 2,
    image: "/images/expodemo/step2.png",
    title: "Enter your email",
    description:
      "Please use the email you used when purchasing the Starter Cats to claim your collectible.",
  },
  {
    id: 3,
    image: "/images/expodemo/step3.png",
    title: "Claim your collectible",
    description:
      "Tap <span class='font-bold text-black'>Claim Now</span> and your digital collectible will be linked to your account.",
  },
];

type DrawerType =
  | "claim_form"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

interface ExpodemoClaimResponse {
  success: boolean;
  action?: "claimed" | "already_claimed";
  message: string;
}

export const ClaimExpoDemo = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { sendRequest } = useHttp();
  const { sendRequest: sendSessionRequest } = useHttp();

  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentSlide(current);
    },
  };

  // Check session on mount and prefill email if logged in
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await sendSessionRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: "/expodemo/session",
          method: "GET",
          responseType: "json",
        });

        if (data.authenticated && data.email) {
          setEmail(data.email);
        }
      } catch (err) {
        // Silently fail - no session
      }
    };

    checkSession();
  }, [sendSessionRequest]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  const handleEmailSubmit = async () => {
    if (!email) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      logger.api("Claiming Expodemo", { email });

      const { data } = await sendRequest<ExpodemoClaimResponse>({
        endpoint: "/expodemo/claim",
        method: "POST",
        payload: { email },
        responseType: "json",
      });

      logger.api("Expodemo claim response received", { response: data });

      if (data.success && data.action === "claimed") {
        setCurrentDrawer("success");
      } else if (data.action === "already_claimed") {
        setError(data.message);
        setCurrentDrawer("already_claimed");
      } else {
        setError(data.message || "Failed to claim");
        setCurrentDrawer("error");
      }
    } catch (err: any) {
      logger.apiError("Failed to claim expodemo", err as Error);
      setError(err instanceof Error ? err.message : "Failed to claim");
      setCurrentDrawer("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-5">
      <div className="px-6 mb-24">
        <div className="relative mx-auto w-full max-w-[340px]">
          <img
            src="/images/expodemo/cert_of_auth.png"
            alt="223 Authenticity Certificate"
            className="w-full"
          />

          <div
            className="w-full absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[30%] rounded-[19.25px] px-5 py-4 flex flex-col items-center gap-3 backdrop-blur-[10px]"
            style={{
              border: '0.5px solid transparent',
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.04), rgba(255,255,255,0.04)), linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <h3 className="text-xl font-bold text-center text-black">
              Authenticity Certificate
            </h3>

            <button
              onClick={() => setCurrentDrawer("claim_form")}
              className="bg-[#1F1F1F] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
            >
              Claim Now
            </button>
          </div>
        </div>
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How It Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <CarouselCard key={card.id} card={card} />
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>

          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${currentSlide === index
                ? "w-[37px] h-[10px] bg-[#000000]"
                : "w-[10px] h-[10px] bg-[#CCCCCC]"
                }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {/* Claim Form Drawer */}
      {currentDrawer === "claim_form" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Claim with your email
              </div>

              <div className="text-[#626262]">
                Please enter your email address to claim your reward.
              </div>

              <div className="!mt-6 relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full pl-[46px] pr-4 border border-[#B4B4B4] focus:outline-none h-11 py-2.5 text-base rounded-[8px]"
                />

                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 -bottom-0.5">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M20.34 6.8206L12.7644 13.0942C12.5492 13.2726 12.2784 13.3702 11.9988 13.3702C11.7193 13.3702 11.4485 13.2726 11.2332 13.0942L3.66122 6.8206C3.6206 6.9429 3.59993 7.07094 3.60002 7.1998V16.7998C3.60002 17.1181 3.72645 17.4233 3.9515 17.6483C4.17654 17.8734 4.48176 17.9998 4.80002 17.9998H19.2C19.5183 17.9998 19.8235 17.8734 20.0486 17.6483C20.2736 17.4233 20.4 17.1181 20.4 16.7998V7.1998C20.4005 7.071 20.3802 6.94297 20.34 6.8206ZM4.80002 4.7998H19.2C19.8365 4.7998 20.447 5.05266 20.8971 5.50275C21.3472 5.95284 21.6 6.56328 21.6 7.1998V16.7998C21.6 17.4363 21.3472 18.0468 20.8971 18.4969C20.447 18.9469 19.8365 19.1998 19.2 19.1998H4.80002C4.1635 19.1998 3.55306 18.9469 3.10297 18.4969C2.65288 18.0468 2.40002 17.4363 2.40002 16.7998V7.1998C2.40002 6.56328 2.65288 5.95284 3.10297 5.50275C3.55306 5.05266 4.1635 4.7998 4.80002 4.7998ZM4.54802 5.9998L11.2392 11.5234C11.4535 11.7004 11.7226 11.7975 12.0006 11.7982C12.2785 11.7988 12.5481 11.703 12.7632 11.527L19.5216 5.9998H4.54802Z"
                      fill="black"
                    />
                  </svg>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              <button
                onClick={handleEmailSubmit}
                disabled={!email || isSubmitting}
                className="py-3 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed !mt-6 rounded-[6px]"
              >
                {isSubmitting ? "Connecting..." : "Connect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claiming (Processing) Drawer */}
      {currentDrawer === "claiming" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">Claiming</div>

              <div className="text-[#626262]">
                Claiming your Digital Collectible. Please wait.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Drawer */}
      {currentDrawer === "success" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] overflow-hidden p-6 pb-10">
            <div className="text-center">
              <div className="text-xl font-bold">🎉 CONGRATS!</div>

              <div className="text-sm text-[#626262] mt-2">
                You should receive your certificate via email shortly. You can
                also view your certificate{" "}
                <span
                  className="text-black underline cursor-pointer"
                  onClick={() => navigate("/rewards/expodemo")}
                >
                  here
                </span>
              </div>
            </div>

            <div className="mt-4">
              <img
                src="/images/expodemo/congrats.png"
                alt="success"
                className="w-full rounded-[14px]"
              />
            </div>

            <div>
              <div className="text-center mt-4 mb-5">
                <div className="font-bold text-[22px]">223</div>
                <div className="text-lg text-[#626262]">
                  Certificate Of Authenticity
                </div>
              </div>

              <button
                onClick={() => navigate("/rewards/expodemo")}
                className="w-full h-10 font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                223 Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Claimed / Error Drawer */}
      {(currentDrawer === "already_claimed" || currentDrawer === "error") && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img
              src="/images/expodemo/error.png"
              alt="error"
              className="mb-4 max-w-[180px]"
            />

            <div className="text-center space-y-2.5 w-full">
              <div className="text-2xl font-bold text-black">Error</div>

              <div className="text-[#626262]">
                {error ||
                  "This collectible has already been claimed or is invalid."}
              </div>

              <button
                onClick={() => {
                  setError(null);
                  setCurrentDrawer("claim_form");
                }}
                className="py-3 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6 rounded-[6px]"
              >
                Please Try Again
              </button>

              <div className="flex items-center justify-center gap-1 text-sm text-gray-500 !mt-4">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>

                <span>
                  Please contact support if you believe this is an error.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarouselCard = ({
  card,
}: {
  card: { id: number; image: string; title: string; description: string };
}) => {
  return (
    <div className="w-full rounded-[14px] overflow-hidden shadow-xl mx-auto">
      <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
        <img
          src={card.image}
          alt={card.title}
          className="object-cover absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};
